import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, getUTM } from "@/lib/api";
import { PhoneCall, CheckCircle2 } from "lucide-react";

const TIME_SLOTS = [
  "As soon as possible",
  "This morning (8am – 12pm)",
  "This afternoon (12pm – 5pm)",
  "Tomorrow morning",
  "Tomorrow afternoon",
  "Weekday evening",
];

export default function CallbackForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [time, setTime] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !phone || !time) {
      toast.error("Please complete name, phone and best time.");
      return;
    }
    setLoading(true);

    // Robust submit: retry up to 3 times with backoff so transient blips don't surface to the patient.
    const utm = await getUTM();
    let ok = false;
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await api.post(
          "/callbacks",
          { name, phone, best_time: time, reason, source: utm.source || "callback-form" },
          { timeout: 25000 }
        );
        // Fire the lead in parallel — non-blocking, errors here shouldn't fail the callback flow
        api.post(
          "/leads",
          { name, phone, message: reason, service: "Callback request", source: utm.source || "callback-form", campaign: utm.campaign, medium: utm.medium },
          { timeout: 25000 }
        ).catch(() => {});
        ok = true;
        break;
      } catch (err) {
        lastErr = err;
        console.warn(`Callback submit attempt ${attempt}/3 failed`, err?.response?.status, err?.message);
        if (attempt < 3) await new Promise((r) => setTimeout(r, 800 * attempt));
      }
    }
    setLoading(false);
    if (ok) {
      setDone(true);
      toast.success("We'll call you back shortly.");
    } else {
      console.error("Callback failed after retries:", lastErr);
      toast.error("Could not submit right now. Please call 021 224 0204 or WhatsApp 072 399 4711.");
    }
  };

  if (done) {
    return (
      <div className="bg-sage/40 border border-sage rounded-xl p-8 text-center" data-testid="callback-success">
        <CheckCircle2 className="w-10 h-10 text-forest mx-auto" />
        <h3 className="font-display text-2xl text-forest mt-3">We've got you.</h3>
        <p className="text-ink/80 mt-2 text-sm">
          A team member will call <span className="font-medium">{phone}</span> at <span className="font-medium">{time.toLowerCase()}</span>.
        </p>
        <Button
          variant="outline"
          className="mt-5 border-forest text-forest hover:bg-forest hover:text-cream"
          onClick={() => { setDone(false); setName(""); setPhone(""); setTime(""); setReason(""); }}
          data-testid="callback-new"
        >
          Submit another
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="callback-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cb-name" className="text-xs uppercase tracking-widest text-ink/70">Full name</Label>
          <Input id="cb-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-cream border-border" placeholder="Jane Mokoena" data-testid="callback-name-input" />
        </div>
        <div>
          <Label htmlFor="cb-phone" className="text-xs uppercase tracking-widest text-ink/70">Phone</Label>
          <Input id="cb-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 bg-cream border-border" placeholder="082 123 4567" data-testid="callback-phone-input" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-ink/70">Best time to call</Label>
        <Select value={time} onValueChange={setTime}>
          <SelectTrigger className="mt-1 bg-cream border-border" data-testid="callback-time-select">
            <SelectValue placeholder="Choose a time window" />
          </SelectTrigger>
          <SelectContent>
            {TIME_SLOTS.map((t) => (
              <SelectItem key={t} value={t} data-testid={`callback-time-${t.split(" ")[0].toLowerCase()}`}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="cb-reason" className="text-xs uppercase tracking-widest text-ink/70">Reason (optional)</Label>
        <Textarea id="cb-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 bg-cream border-border" placeholder="e.g. repeat script, vaccinations, general check-up" rows={3} data-testid="callback-reason-input" />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-clay hover:bg-clay-dark text-cream h-11 rounded-lg" data-testid="callback-submit">
        <PhoneCall className="w-4 h-4 mr-2" />
        {loading ? "Sending..." : "Request my callback"}
      </Button>
      <p className="text-[11px] text-ink/60 text-center">
        By submitting, you consent to us contacting you about your request.
      </p>
    </form>
  );
}
